/**
 * Download Utilities
 * Reusable utilities for handling file downloads across the application
 */

import { buildUrl } from '../config/services.config';
import { authService } from '../services/auth.service';

export interface DownloadOptions {
  onProgress?: (progress: number) => void;
  onError?: (error: Error) => void;
  onSuccess?: () => void;
}

/**
 * Download a file from the backend using file_id
 * Handles authentication, blob creation, and programmatic download with progress tracking
 */
export async function downloadFile(
  fileId: string,
  fileName: string,
  options?: DownloadOptions
): Promise<void> {
  try {
    const response = await fetch(buildUrl('backend', `/api/v1/files/download/${fileId}`), {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${authService.getAccessToken()}`
      }
    });

    if (!response.ok) {
      throw new Error(`Failed to download file: ${response.status} ${response.statusText}`);
    }

    // Get content length for progress tracking
    const contentLength = response.headers.get('content-length');
    const total = contentLength ? parseInt(contentLength, 10) : 0;

    // Read the response as a stream with progress tracking
    const reader = response.body?.getReader();
    const chunks: Uint8Array[] = [];
    let receivedLength = 0;

    if (!reader) {
      throw new Error('Unable to read response body');
    }

    // Report initial progress
    if (options?.onProgress) {
      options.onProgress(0);
    }

    while (true) {
      const { done, value } = await reader.read();

      if (done) break;

      chunks.push(value);
      receivedLength += value.length;

      // Report progress if total size is known
      if (total > 0 && options?.onProgress) {
        const progress = (receivedLength / total) * 100;
        options.onProgress(Math.min(progress, 99)); // Cap at 99% until complete
      }
    }

    // Combine chunks into a single Uint8Array
    const chunksAll = new Uint8Array(receivedLength);
    let position = 0;
    for (const chunk of chunks) {
      chunksAll.set(chunk, position);
      position += chunk.length;
    }

    // Create blob from the combined data
    const blob = new Blob([chunksAll]);

    // Create a temporary URL for the blob
    const url = window.URL.createObjectURL(blob);

    // Create a temporary anchor element and trigger download
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();

    // Cleanup
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    // Report 100% progress
    if (options?.onProgress) {
      options.onProgress(100);
    }

    // Call success callback if provided
    if (options?.onSuccess) {
      options.onSuccess();
    }
  } catch (error) {
    console.error('Download failed:', error);

    // Call error callback if provided
    if (options?.onError) {
      options.onError(error as Error);
    } else {
      // Re-throw if no error handler provided
      throw error;
    }
  }
}

/**
 * Download multiple files sequentially
 */
export async function downloadMultipleFiles(
  files: Array<{ fileId: string; fileName: string }>,
  options?: DownloadOptions
): Promise<void> {
  for (let i = 0; i < files.length; i++) {
    const file = files[i];

    // Calculate progress
    if (options?.onProgress) {
      const progress = ((i + 1) / files.length) * 100;
      options.onProgress(progress);
    }

    await downloadFile(file.fileId, file.fileName, {
      onError: options?.onError,
      onSuccess: i === files.length - 1 ? options?.onSuccess : undefined
    });

    // Add a small delay between downloads to avoid overwhelming the browser
    if (i < files.length - 1) {
      await new Promise(resolve => setTimeout(resolve, 500));
    }
  }
}

/**
 * Get file extension from filename
 */
export function getFileExtension(fileName: string): string {
  const parts = fileName.split('.');
  return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : '';
}

/**
 * Get file type icon based on extension
 */
export function getFileTypeIcon(fileName: string): string {
  const ext = getFileExtension(fileName);

  const iconMap: { [key: string]: string } = {
    pdf: '📄',
    doc: '📝',
    docx: '📝',
    xls: '📊',
    xlsx: '📊',
    csv: '📊',
    txt: '📃',
    jpg: '🖼️',
    jpeg: '🖼️',
    png: '🖼️',
    gif: '🖼️',
    zip: '📦',
    rar: '📦',
    '7z': '📦'
  };

  return iconMap[ext] || '📎';
}

/**
 * Format file size from bytes to human-readable format
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
}
